/**
 * SubBranchMaster.jsx
 *
 * ✅ React Add + Edit page for SubBranchMaster.aspx/.cs
 * ✅ Uses your Admin template style (HeaderBar + AdminLayoutWrapper + theme skins)
 * ✅ Matches ASPX logic:
 *   - Load init dropdowns (countries, branches, main branch count & limit)
 *   - If edit (branchId param), load sub-branch details and pre-fill form
 *   - Cascading dropdowns: Country -> State -> District -> City
 *   - "Others" option switches to textbox (state/district/city)
 *   - Validations like ASPX Validate()
 *   - Submit calls Node API: POST /api/subbranch-master/save
 *
 * IMPORTANT:
 * - Multiple DB logic is in Node and commented there (as you asked).
 * - For "Is Main Branch" checkbox, ASPX has it disabled; we keep disabled by default.
 */

import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaCheck, FaChevronDown, FaChevronUp } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

const SubBranchMaster = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const { branchId } = useParams(); // edit if present
  const isEdit = useMemo(() => !!branchId, [branchId]);

  /** ---------------- UI State ---------------- */
  const [showForm, setShowForm] = useState(true);
  const [loadingInit, setLoadingInit] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [saving, setSaving] = useState(false);

  /** ---------------- Dropdown Data ---------------- */
  const [branches, setBranches] = useState([]); // ddl_branch
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);

  /** ---------------- Init Flags ---------------- */
  const [mainBranchCount, setMainBranchCount] = useState(0);
  const [isMainCheckboxEnabled, setIsMainCheckboxEnabled] = useState(false);
  const [subbranchLimit, setSubbranchLimit] = useState({ allowed: 0, current: 0, canCreate: true });

  /** ---------------- Form State ----------------
   * We model UI with "mode" flags for Others selection:
   * - stateMode: dropdown/text
   * - districtMode: dropdown/text
   * - cityMode: dropdown/text
   */
  const [form, setForm] = useState({
    mainBranchId: "Select",
    branchName: "",
    branchCode: "",
    address1: "",
    address2: "",
    zipcode: "",
    email: "",
    phone: "",
    mobile: "",
    countryId: "Select",

    stateMode: "dropdown",
    stateId: "Select",
    stateName: "",

    districtMode: "dropdown",
    districtId: "Select",
    districtName: "",

    cityMode: "dropdown",
    cityId: "Select",
    cityName: "",

    tinNo: "",
    gstNo: "",
    deliveryAddress: "",
    remarks: "",
    isMainBranch: false, // ASPX checkbox is disabled by default
    autoStnApprove: false,
  });

  const [errors, setErrors] = useState({});

  /** ---------------- Helpers ---------------- */
  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validateEmail = (v) => {
    if (!String(v || "").trim()) return true;
    const emailPattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return emailPattern.test(String(v).trim());
  };

  const validateForm = () => {
    const e = {};
    if (!String(form.branchName || "").trim()) e.branchName = "Please enter Branch Name";
    if (!String(form.address1 || "").trim()) e.address1 = "Please enter Address1";
    if (String(form.countryId) === "Select") e.countryId = "Please Select Country";
    if (!String(form.tinNo || "").trim()) e.tinNo = "Please enter TIN No";

    // State required based on mode (ASPX checks display inline)
    if (form.stateMode === "dropdown") {
      if (String(form.stateId) === "Select") e.stateId = "Please Select State";
    } else {
      if (!String(form.stateName || "").trim()) e.stateName = "Please enter State";
    }

    if (!String(form.zipcode || "").trim()) e.zipcode = "Please enter ZipCode";

    if (!validateEmail(form.email)) e.email = "Please enter valid emailid";

    if (!String(form.phone || "").trim()) e.phone = "Please enter Phone";

    if (String(form.mainBranchId) === "Select") e.mainBranchId = "Please select Branchname";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /** ---------------- API Calls ---------------- */

  // init (countries + branches + limit)
  const fetchInit = async () => {
    try {
      setLoadingInit(true);
      const res = await axios.get(`${BASE_URL}/api/subbranch-master/init`, {
        headers: tenantHeaders(),
        params: tenantParams(),
      });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load init data");
        return;
      }

      const d = res.data.data;
      setMainBranchCount(Number(d.mainBranchCount || 0));
      setIsMainCheckboxEnabled(!!d.isMainCheckboxEnabled);
      setSubbranchLimit(d.subbranchLimit || { allowed: 0, current: 0, canCreate: true });

      setCountries(d.countries || []);
      setBranches(d.branches || []);

      // ASPX: if cannot create, redirect to listing
      if (!isEdit && d.subbranchLimit && d.subbranchLimit.canCreate === false) {
        toast.error(`You can create only ${d.subbranchLimit.current} Sub branches`);
        navigate("/adminMaster/SubbranchListing");
      }
    } catch (err) {
      console.error("Init error:", err);
      toast.error("Failed to load init data");
    } finally {
      setLoadingInit(false);
    }
  };

  const fetchStates = async (countryId) => {
    if (!countryId || countryId === "Select") {
      setStates([]);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/subbranch-master/states`, {
        headers: tenantHeaders(),
        params: tenantParams({ countryId }),
      });      if (res.data?.success) setStates(res.data.list || []);
    } catch (err) {
      console.error("States error:", err);
      toast.error("Failed to load states");
    }
  };

  const fetchDistricts = async (stateId) => {
    if (!stateId || stateId === "Select") {
      setDistricts([]);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/subbranch-master/districts`, {
        headers: tenantHeaders(),
        params: tenantParams({ stateId }),
      });      if (res.data?.success) setDistricts(res.data.list || []);
    } catch (err) {
      console.error("Districts error:", err);
      toast.error("Failed to load districts");
    }
  };

  const fetchCities = async (districtId) => {
    if (!districtId || districtId === "Select") {
      setCities([]);
      return;
    }
    try {
      const res = await axios.get(`${BASE_URL}/api/subbranch-master/cities`, {
        headers: tenantHeaders(),
        params: tenantParams({ districtId }),
      });      if (res.data?.success) setCities(res.data.list || []);
    } catch (err) {
      console.error("Cities error:", err);
      toast.error("Failed to load cities");
    }
  };

  // load edit details
  const fetchEdit = async (id) => {
    try {
      setLoadingEdit(true);

      const res = await axios.get(`${BASE_URL}/api/subbranch-master/${id}`, {
        headers: tenantHeaders(),
        params: tenantParams(),
      });      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load branch");
        return;
      }

      const d = res.data.data;

      // Populate form
      setForm((prev) => ({
        ...prev,
        mainBranchId: d.fld_mainbranchid ?? "Select",
        branchName: d.fld_branchname || "",
        branchCode: d.fld_branchcode || "",
        address1: d.fld_address1 || "",
        address2: d.fld_address2 || "",
        zipcode: d.fld_zipcode || "",
        email: d.fld_email || "",
        phone: d.fld_phoneno || "",
        mobile: d.fld_mobile || "",
        remarks: d.fld_remarks || "",
        tinNo: d.fld_tinno || "",
        gstNo: d.fld_cstno || "",
        deliveryAddress: d.fld_deliveryaddress || "",
        countryId: d.fld_countryid ?? "Select",
        stateMode: "dropdown",
        stateId: d.fld_stateid ?? "Select",
        districtMode: "dropdown",
        districtId: d.fld_districtid ?? "Select",
        cityMode: "dropdown",
        cityId: d.fld_cityid ?? "Select",
        isMainBranch: d.fld_IsMainBranch === true || d.fld_IsMainBranch === 1,
        autoStnApprove: String(d.fld_AutoSTNApprove) === "1",
      }));

      // Cascade fetch (same as ASPX BindBranchDetails)
      if (d.fld_countryid) await fetchStates(d.fld_countryid);
      if (d.fld_stateid) await fetchDistricts(d.fld_stateid);
      if (d.fld_districtid) await fetchCities(d.fld_districtid);
    } catch (err) {
      console.error("Edit load error:", err);
      toast.error("Failed to load branch");
    } finally {
      setLoadingEdit(false);
    }
  };

  /** ---------------- Effects ---------------- */
  useEffect(() => {
    fetchInit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On init done + edit mode, load edit data
  useEffect(() => {
    if (isEdit && branchId && !loadingInit) {
      fetchEdit(branchId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, branchId, loadingInit]);

  /** ---------------- Dropdown change handlers ---------------- */

  const onCountryChange = async (value) => {
    setField("countryId", value);

    // reset cascading
    setStates([]);
    setDistricts([]);
    setCities([]);

    setForm((prev) => ({
      ...prev,
      stateMode: "dropdown",
      stateId: "Select",
      stateName: "",
      districtMode: "dropdown",
      districtId: "Select",
      districtName: "",
      cityMode: "dropdown",
      cityId: "Select",
      cityName: "",
    }));

    await fetchStates(value);
  };

  const onStateChange = async (value) => {
    if (value === "Others") {
      // switch to textbox mode for state/district/city
      setForm((prev) => ({
        ...prev,
        stateMode: "text",
        stateId: "Select",
        stateName: "",
        districtMode: "text",
        districtId: "Select",
        districtName: "",
        cityMode: "text",
        cityId: "Select",
        cityName: "",
      }));
      setDistricts([]);
      setCities([]);
      return;
    }

    setForm((prev) => ({
      ...prev,
      stateMode: "dropdown",
      stateId: value,
      stateName: "",
      districtMode: "dropdown",
      districtId: "Select",
      districtName: "",
      cityMode: "dropdown",
      cityId: "Select",
      cityName: "",
    }));

    setDistricts([]);
    setCities([]);
    await fetchDistricts(value);
  };

  const onDistrictChange = async (value) => {
    if (value === "Others") {
      setForm((prev) => ({
        ...prev,
        districtMode: "text",
        districtId: "Select",
        districtName: "",
        cityMode: "text",
        cityId: "Select",
        cityName: "",
      }));
      setCities([]);
      return;
    }

    setForm((prev) => ({
      ...prev,
      districtMode: "dropdown",
      districtId: value,
      districtName: "",
      cityMode: "dropdown",
      cityId: "Select",
      cityName: "",
    }));

    setCities([]);
    await fetchCities(value);
  };

  const onCityChange = (value) => {
    if (value === "Others") {
      setForm((prev) => ({
        ...prev,
        cityMode: "text",
        cityId: "Select",
        cityName: "",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      cityMode: "dropdown",
      cityId: value,
      cityName: "",
    }));
  };

  /** ---------------- Submit ---------------- */
  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fill required fields");
      return;
    }

    // AdminId (ASPX uses Session["AdminID"])


    const admin = JSON.parse(localStorage.getItem("admin") || "{}");
const adminId = Number(admin?.id || 0);

if (!adminId) {
  toast.error("Admin session missing. Please login again.");
  return;
}


    try {
      setSaving(true);

      const payload = {
        branchId: isEdit ? Number(branchId) : 0,
        mainBranchId: Number(form.mainBranchId),
        branchName: form.branchName.trim(),
        branchCode: String(form.branchCode || "").trim(),
        address1: form.address1.trim(),
        address2: String(form.address2 || "").trim(),
        countryId: Number(form.countryId),

        stateMode: form.stateMode,
        stateId: form.stateMode === "dropdown" ? Number(form.stateId) : undefined,
        stateName: form.stateMode === "text" ? form.stateName.trim() : undefined,

        districtMode: form.districtMode,
        districtId: form.districtMode === "dropdown" ? Number(form.districtId) : undefined,
        districtName: form.districtMode === "text" ? form.districtName.trim() : undefined,

        cityMode: form.cityMode,
        cityId: form.cityMode === "dropdown" ? Number(form.cityId) : undefined,
        cityName: form.cityMode === "text" ? form.cityName.trim() : undefined,

        zipcode: String(form.zipcode || "").trim(),
        phone: String(form.phone || "").trim(),
        mobile: String(form.mobile || "").trim(),
        email: String(form.email || "").trim(),

        tinNo: String(form.tinNo || "").trim(),
        gstNo: String(form.gstNo || "").trim(),
        deliveryAddress: String(form.deliveryAddress || "").trim(),
        remarks: String(form.remarks || "").trim(),

        isMainBranch: !!form.isMainBranch,
        autoStnApprove: !!form.autoStnApprove,

        adminId,
      };

      const res = await axios.post(`${BASE_URL}/api/subbranch-master/save`, payload, {
        headers: tenantHeaders(),
      });
      if (!res.data?.success) {
        toast.error(res.data?.message || "Save failed");
        return;
      }

      toast.success(res.data?.message || "Saved");

      // ASPX redirects:
      // - Add => SubBranchListing.aspx
      // - Update => BranchListing.aspx (we will redirect to sub branch listing for now)
      navigate("/adminMaster/SubbranchListing");
    } catch (err) {
      console.error("Submit error:", err);
      toast.error(err.response?.data?.message || "Server error");
    } finally {
      setSaving(false);
    }
  };

  /** ---------------- Render ---------------- */

  const pageTitle = isEdit ? "SUB BRANCH MASTER (EDIT)" : "SUB BRANCH MASTER (ADD)";

  const inputBase =
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ` +
    `${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`;

  const selectBase =
    `w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ` +
    `${isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300 text-gray-900"}`;

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">{pageTitle}</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          {/* Top Actions */}
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Sub Branch Master
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {isEdit ? `Editing ID: ${branchId}` : "New Entry"}
              </span>
            </div>

            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => navigate("/adminMaster/SubbranchListing")}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                <FaArrowLeft />
                Back
              </button>

              <button
                onClick={() => setShowForm(!showForm)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showForm ? <FaChevronUp /> : <FaChevronDown />}
                {showForm ? "Hide Form" : "Show Form"}
              </button>
            </div>
          </div>

          {/* Loading banners */}
          {(loadingInit || loadingEdit) && (
            <div
              className={`mb-6 p-4 rounded-lg border ${
                isDark ? "bg-gray-800 border-gray-700 text-gray-200" : "bg-gray-50 border-gray-200 text-gray-700"
              }`}
            >
              Loading...
            </div>
          )}

          {/* Form */}
          {showForm && (
            <div
              className={`rounded-xl border overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div className={`p-6 ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className="grid grid-cols-12 gap-4">
                  {/* Branch (Main Branch dropdown) */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Branch Name <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.mainBranchId}
                      onChange={(e) => setField("mainBranchId", e.target.value)}
                      className={`${selectBase} ${errors.mainBranchId ? "border-red-500" : ""}`}
                      disabled={saving}
                    >
                      <option value="Select">Select</option>
                      {branches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                    {errors.mainBranchId && <p className="text-red-500 text-sm mt-1">{errors.mainBranchId}</p>}
                  </div>

                  {/* Sub Branch Name */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Sub Branch Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.branchName}
                      onChange={(e) => setField("branchName", e.target.value)}
                      className={`${inputBase} ${errors.branchName ? "border-red-500" : ""}`}
                      disabled={saving}
                      placeholder="Enter sub branch name..."
                    />
                    {errors.branchName && <p className="text-red-500 text-sm mt-1">{errors.branchName}</p>}
                  </div>

                  {/* Branch Code */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Branch Code
                    </label>
                    <input
                      value={form.branchCode}
                      onChange={(e) => setField("branchCode", e.target.value)}
                      className={inputBase}
                      disabled={saving}
                      placeholder="Enter branch code..."
                    />
                  </div>

                  {/* Is Main Branch */}
                  <div className="col-span-12 md:col-span-6 flex items-end">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={!!form.isMainBranch}
                        onChange={(e) => setField("isMainBranch", e.target.checked)}
                        disabled={saving || !isMainCheckboxEnabled} // ASPX disables in most cases
                        className={`w-5 h-5 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                      />
                      <div>
                        <div className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                          Is Main Branch
                        </div>
                        <div className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                          {mainBranchCount === 1
                            ? "Disabled because only 1 main branch exists"
                            : "Enabled only if system allows"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Zipcode */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Zipcode <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.zipcode}
                      onChange={(e) => setField("zipcode", e.target.value.replace(/[^\d]/g, "").slice(0, 6))}
                      className={`${inputBase} ${errors.zipcode ? "border-red-500" : ""}`}
                      disabled={saving}
                      placeholder="Enter zipcode..."
                    />
                    {errors.zipcode && <p className="text-red-500 text-sm mt-1">{errors.zipcode}</p>}
                  </div>

                  {/* Address1 */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Address 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.address1}
                      onChange={(e) => setField("address1", e.target.value)}
                      className={`${inputBase} ${errors.address1 ? "border-red-500" : ""}`}
                      disabled={saving}
                      placeholder="Enter address1..."
                    />
                    {errors.address1 && <p className="text-red-500 text-sm mt-1">{errors.address1}</p>}
                  </div>

                  {/* Email */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Email
                    </label>
                    <input
                      value={form.email}
                      onChange={(e) => setField("email", e.target.value)}
                      className={`${inputBase} ${errors.email ? "border-red-500" : ""}`}
                      disabled={saving}
                      placeholder="Enter email..."
                    />
                    {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                  </div>

                  {/* Address2 */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Address 2
                    </label>
                    <input
                      value={form.address2}
                      onChange={(e) => setField("address2", e.target.value)}
                      className={inputBase}
                      disabled={saving}
                      placeholder="Enter address2..."
                    />
                  </div>

                  {/* Phone */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.phone}
                      onChange={(e) => setField("phone", e.target.value)}
                      className={`${inputBase} ${errors.phone ? "border-red-500" : ""}`}
                      disabled={saving}
                      placeholder="Enter phone..."
                    />
                    {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
                  </div>

                  {/* Country */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={form.countryId}
                      onChange={(e) => onCountryChange(e.target.value)}
                      className={`${selectBase} ${errors.countryId ? "border-red-500" : ""}`}
                      disabled={saving}
                    >
                      <option value="Select">Select</option>
                      {countries.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    {errors.countryId && <p className="text-red-500 text-sm mt-1">{errors.countryId}</p>}
                  </div>

                  {/* Mobile */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Mobile
                    </label>
                    <input
                      value={form.mobile}
                      onChange={(e) => setField("mobile", e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
                      className={inputBase}
                      disabled={saving}
                      placeholder="Enter mobile..."
                    />
                  </div>

                  {/* State */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      State <span className="text-red-500">*</span>
                    </label>

                    {form.stateMode === "dropdown" ? (
                      <select
                        value={form.stateId}
                        onChange={(e) => onStateChange(e.target.value)}
                        className={`${selectBase} ${errors.stateId ? "border-red-500" : ""}`}
                        disabled={saving || form.countryId === "Select"}
                      >
                        <option value="Select">Select</option>
                        {states.map((s) => (
                          <option key={s.id} value={s.id}>
                            {s.name}
                          </option>
                        ))}
                        <option value="Others">Others</option>
                      </select>
                    ) : (
                      <input
                        value={form.stateName}
                        onChange={(e) => setField("stateName", e.target.value)}
                        className={`${inputBase} ${errors.stateName ? "border-red-500" : ""}`}
                        disabled={saving}
                        placeholder="Enter state..."
                      />
                    )}

                    {(errors.stateId || errors.stateName) && (
                      <p className="text-red-500 text-sm mt-1">{errors.stateId || errors.stateName}</p>
                    )}
                  </div>

                  {/* TIN */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      TIN No <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.tinNo}
                      onChange={(e) => setField("tinNo", e.target.value)}
                      className={`${inputBase} ${errors.tinNo ? "border-red-500" : ""}`}
                      disabled={saving}
                      placeholder="Enter TIN..."
                    />
                    {errors.tinNo && <p className="text-red-500 text-sm mt-1">{errors.tinNo}</p>}
                  </div>

                  {/* District */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      District
                    </label>

                    {form.districtMode === "dropdown" ? (
                      <select
                        value={form.districtId}
                        onChange={(e) => onDistrictChange(e.target.value)}
                        className={selectBase}
                        disabled={saving || form.stateMode !== "dropdown" || form.stateId === "Select"}
                      >
                        <option value="Select">Select</option>
                        {districts.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.name}
                          </option>
                        ))}
                        <option value="Others">Others</option>
                      </select>
                    ) : (
                      <input
                        value={form.districtName}
                        onChange={(e) => setField("districtName", e.target.value)}
                        className={inputBase}
                        disabled={saving}
                        placeholder="Enter district..."
                      />
                    )}
                  </div>

                  {/* City */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      City
                    </label>

                    {form.cityMode === "dropdown" ? (
                      <select
                        value={form.cityId}
                        onChange={(e) => onCityChange(e.target.value)}
                        className={selectBase}
                        disabled={saving || form.districtMode !== "dropdown" || form.districtId === "Select"}
                      >
                        <option value="Select">Select</option>
                        {cities.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}
                          </option>
                        ))}
                        <option value="Others">Others</option>
                      </select>
                    ) : (
                      <input
                        value={form.cityName}
                        onChange={(e) => setField("cityName", e.target.value)}
                        className={inputBase}
                        disabled={saving}
                        placeholder="Enter city..."
                      />
                    )}
                  </div>

                  {/* GST */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      GST No <span className="text-red-500">*</span>
                    </label>
                    <input
                      value={form.gstNo}
                      onChange={(e) => setField("gstNo", e.target.value)}
                      className={inputBase}
                      disabled={saving}
                      placeholder="Enter GST..."
                    />
                  </div>

                  {/* Delivery Address */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Delivery Address
                    </label>
                    <textarea
                      value={form.deliveryAddress}
                      onChange={(e) => setField("deliveryAddress", e.target.value)}
                      className={`${inputBase} min-h-[90px]`}
                      disabled={saving}
                      placeholder="Enter delivery address..."
                    />
                  </div>

                  {/* Remarks */}
                  <div className="col-span-12 md:col-span-6">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Remarks
                    </label>
                    <textarea
                      value={form.remarks}
                      onChange={(e) => setField("remarks", e.target.value)}
                      className={`${inputBase} min-h-[90px]`}
                      disabled={saving}
                      placeholder="Enter remarks..."
                    />
                  </div>

                  {/* Auto STN Approve */}
                  <div className="col-span-12 md:col-span-6 flex items-center gap-3 mt-2">
                    <input
                      type="checkbox"
                      checked={!!form.autoStnApprove}
                      onChange={(e) => setField("autoStnApprove", e.target.checked)}
                      disabled={saving}
                      className={`w-5 h-5 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                    />
                    <div className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Auto STN Approve
                    </div>
                  </div>
                </div>

                {/* Submit */}
                <div className="flex justify-center mt-8">
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={saving}
                    className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white disabled:opacity-60`}
                  >
                    <FaCheck />
                    {saving ? "Saving..." : isEdit ? "Update" : "Submit"}
                  </button>
                </div>

                {/* Limit info */}
                {!isEdit && (
                  <div className={`mt-6 text-center text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                    Allowed Sub Branches: <b>{subbranchLimit.allowed}</b> | Current:{" "}
                    <b>{subbranchLimit.current}</b>
                  </div>
                )}
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </AdminLayoutWrapper>
  );
};

export default SubBranchMaster;
